import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao } from '../../componentes/base';
import { Carregando, Erro } from '../../componentes/estados';
import { CampoControlado, CampoData, Entrada, Rotulo, EntradaSensivel } from '../../componentes/formulario';
import { Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { get, mensagemDeErro, post } from '../../nucleo/api';
import { dinheiro, nomeCadastro, numero } from '../../nucleo/formato';
import { useComEstoque } from './AvisoModo';
import { useDepositos } from './comum';
import { hojeLocal, invalidarCompras, numeroDe, qtd, type PedidoDetalhe } from './tipos';
import { useValidacaoLocal } from '../../componentes/validacao-local';
import { TextoCortado } from '../../componentes/texto-cortado';
import { anunciarOcultos } from '../../componentes/sensivel';

interface LinhaRecebida {
  pedido_item_id: string;
  quantidade: string;
  lote: string;
  validade: string;
  conferido: boolean;
  observacao: string;
}

/**
 * Registro do recebimento, parcial ou total, conferido com o pedido (M10.3). A quantidade já vem com o que falta chegar
 * e não passa disso. O recebimento gera a entrada no Estoque ao preço do pedido e a conta a pagar em Contas.
 * O id do recebimento e a Idempotency-Key nascem com a tela: reenviar por engano nunca recebe duas vezes.
 */
export function ModalRecebimento({ pedidoId, aoFechar, aoRegistrar }: { pedidoId: string | null; aoFechar: () => void; aoRegistrar?: (id: string) => void }) {
  if (!pedidoId) return null;
  return <Corpo pedidoId={pedidoId} aoFechar={aoFechar} aoRegistrar={aoRegistrar} />;
}

function Corpo({ pedidoId, aoFechar, aoRegistrar }: { pedidoId: string; aoFechar: () => void; aoRegistrar?: (id: string) => void }) {
  const { t } = useTranslation('compras');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  // Fase 2 (W25, K14): sem o Estoque, o recebimento confirma o pedido e gera a conta a pagar; depósito e lote não se aplicam.
  const comEstoque = useComEstoque();
  const depositos = useDepositos(comEstoque);
  const [chave] = useState(() => crypto.randomUUID());
  const q = useQuery({ queryKey: ['/compras/pedidos', 'obter', pedidoId], queryFn: () => get<PedidoDetalhe>(`/compras/pedidos/${pedidoId}`) });
  const d = q.data;
  const [linhas, setLinhas] = useState<LinhaRecebida[]>([]);
  const [data, setData] = useState(hojeLocal());
  const [depositoId, setDepositoId] = useState('');
  const [observacao, setObservacao] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  useValidacaoLocal(null, erro);

  useEffect(() => {
    if (!d) return;
    setLinhas(d.itens_detalhe.filter((i) => i.saldo > 0).map((i) => ({ pedido_item_id: i.id, quantidade: String(i.saldo).replace('.', ','), lote: '', validade: '', conferido: true, observacao: '' })));
    setDepositoId(d.deposito_id ?? '');
  }, [d?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const itemDe = (id: string) => d?.itens_detalhe.find((i) => i.id === id);
  const muda = (id: string, parcial: Partial<LinhaRecebida>) => setLinhas((ls) => ls.map((l) => (l.pedido_item_id === id ? { ...l, ...parcial } : l)));
  const recebendo = linhas.filter((l) => (numeroDe(l.quantidade) ?? 0) > 0);
  const total = recebendo.reduce((a, l) => a + Math.round((numeroDe(l.quantidade) ?? 0) * (itemDe(l.pedido_item_id)?.preco_unitario_centavos ?? 0)), 0);
  const parcelas = d ? (d.condicao_pagamento.includes('/') ? d.condicao_pagamento.split('/').length : 1) : 1;

  const registrar = useMutation({
    mutationFn: async () => {
      for (const l of recebendo) {
        const item = itemDe(l.pedido_item_id)!;
        const q = numeroDe(l.quantidade)!;
        if (q > item.saldo) throw new Error(t('recebimento.acimaDoPedido', { produto: nomeCadastro(item.produto_nome), saldo: qtd(item.saldo, numero) }));
        if (!l.conferido && !l.observacao.trim()) throw new Error(t('recebimento.divergenciaSemTexto', { produto: nomeCadastro(item.produto_nome) }));
        if (l.validade && !l.lote.trim()) throw new Error(t('recebimento.validadeSemLote', { produto: nomeCadastro(item.produto_nome) }));
      }
      if (!recebendo.length) throw new Error(t('recebimento.nadaRecebido'));
      return post<{ id: string }>(
        '/compras/recebimentos',
        {
          id: chave,
          pedido_id: pedidoId,
          data,
          deposito_id: comEstoque ? depositoId || null : null,
          observacao: observacao.trim() || null,
          itens: recebendo.map((l) => ({
            pedido_item_id: l.pedido_item_id,
            quantidade: numeroDe(l.quantidade),
            lote_codigo: l.lote.trim() || null,
            lote_validade: l.validade || null,
            conferido: l.conferido,
            observacao: l.observacao.trim() || null,
          })),
        },
        { 'Idempotency-Key': chave },
      );
    },
    onSuccess: async (r) => {
      await invalidarCompras(qc);
      toast.sucesso(t('mensagens.recebimentoRegistrado'));
      aoRegistrar?.(r.id);
      aoFechar();
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  });

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={d ? t('recebimento.titulo', { numero: d.numero }) : t('recebimento.tituloCurto')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" disabled={!d || recebendo.length === 0} carregando={registrar.isPending} onClick={() => (setErro(null), registrar.mutate())}>
            {t('acoes.registrarRecebimento')}
          </Botao>
        </>
      }
    >
      {q.isPending && <Carregando />}
      {q.isError && <Erro erro={q.error} aoTentar={() => q.refetch()} />}
      {d && (
        <div className="flex min-w-0 flex-col gap-4">
          <p className="text-sm text-tinta-2">{comEstoque ? t('recebimento.explicacao') : t('w25.semEstoqueRecebimento')}</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <CampoControlado def={{ nome: 'data', rotulo: t('campos.dataRecebimento'), tipo: 'data', obrigatorio: true }} valor={data} aoMudar={(v) => setData(String(v ?? ''))} />
            {comEstoque && (
              <CampoControlado
                def={{ nome: 'deposito', rotulo: t('campos.deposito'), tipo: 'selecao', opcoes: (depositos.data?.itens ?? []).map((x) => ({ valor: x.id, rotulo: x.padrao ? `${x.nome} (${t('campos.padrao')})` : x.nome })), ajuda: t('ajuda.depositoRecebimento') }}
                valor={depositoId}
                aoMudar={(v) => setDepositoId(String(v ?? ''))}
              />
            )}
          </div>
          <ul className="flex flex-col gap-3">
            {linhas.map((l) => {
              const item = itemDe(l.pedido_item_id);
              if (!item) return null;
              const q = numeroDe(l.quantidade) ?? 0;
              const passou = q > item.saldo;
              return (
                <li key={l.pedido_item_id} className="grid min-w-0 grid-cols-6 gap-2 rounded-lg border border-borda bg-sup-1 p-3">
                  <div className="col-span-6 flex min-w-0 items-start justify-between gap-3">
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium" title={item.produto_nome ?? undefined}>{nomeCadastro(item.produto_nome)}</span>
                      <TextoCortado className="numero block text-xs text-tinta-3">
                        {item.produto_codigo} · {t('recebimento.faltam', { saldo: qtd(item.saldo, numero), unidade: item.unidade ?? '' })} · {anunciarOcultos(dinheiro(item.preco_unitario_centavos, d.moeda))}
                      </TextoCortado>
                    </span>
                  </div>
                  <div className="col-span-3 sm:col-span-2">
                    <Rotulo id={`rec-qtd-${l.pedido_item_id}`} rotulo={t('recebimento.quantidadeRecebida')} erro={passou ? t('recebimento.maiorQueSaldo') : undefined}>
                      <Entrada id={`rec-qtd-${l.pedido_item_id}`} inputMode="decimal" className="numero text-right" value={l.quantidade} onChange={(e) => muda(l.pedido_item_id, { quantidade: e.target.value })} />
                    </Rotulo>
                  </div>
                  {comEstoque && (
                    <>
                      <div className="col-span-3 sm:col-span-2">
                        <Rotulo id={`rec-lote-${l.pedido_item_id}`} rotulo={t('campos.lote')}>
                          <Entrada id={`rec-lote-${l.pedido_item_id}`} maxLength={60} value={l.lote} onChange={(e) => muda(l.pedido_item_id, { lote: e.target.value })} />
                        </Rotulo>
                      </div>
                      <div className="col-span-6 sm:col-span-2">
                        <Rotulo id={`rec-val-${l.pedido_item_id}`} rotulo={t('campos.validadeLote')}>
                          <CampoData id={`rec-val-${l.pedido_item_id}`} type="date" value={l.validade} onChange={(e) => muda(l.pedido_item_id, { validade: e.target.value })} />
                        </Rotulo>
                      </div>
                    </>
                  )}
                  <label className="col-span-6 flex items-center gap-2 text-sm text-tinta-2">
                    <input type="checkbox" checked={l.conferido} onChange={(e) => muda(l.pedido_item_id, { conferido: e.target.checked })} />
                    {t('recebimento.conferido')}
                  </label>
                  {!l.conferido && (
                    <div className="col-span-6">
                      <Rotulo id={`rec-obs-${l.pedido_item_id}`} rotulo={t('recebimento.divergencia')} obrigatorio>
                        <EntradaSensivel mascara={null} livre id={`rec-obs-${l.pedido_item_id}`} maxLength={300} value={l.observacao} onChange={(e) => muda(l.pedido_item_id, { observacao: e.target.value })} />
                      </Rotulo>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          <Rotulo id="rec-obs-geral" rotulo={t('campos.observacao')}>
            <EntradaSensivel mascara={null} livre id="rec-obs-geral" maxLength={500} value={observacao} onChange={(e) => setObservacao(e.target.value)} />
          </Rotulo>
          <div className="flex min-w-0 flex-col gap-1 rounded-lg border border-borda bg-sup-2 px-3 py-2">
            <span className="kicker">{t('recebimento.valorRecebido')}</span>
            <TextoCortado className="numero text-xl font-semibold text-tinta-1">{anunciarOcultos(dinheiro(total, d.moeda))}</TextoCortado>
            <span className="text-xs text-tinta-3">{d.condicao_pagamento === 'avista' ? t('recebimento.efeitosAvista') : t('recebimento.efeitos', { condicao: d.condicao_pagamento, count: parcelas })}</span>
          </div>
          {erro && (
            <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
              {erro}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
